import React from 'react';
import { Link } from 'react-router-dom';
import LocLink from './LocLink';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { FaEtsy } from 'react-icons/fa';
import { Shield, CheckCircle, ChevronRight, ExternalLink } from 'lucide-react';
import ProductLimitationsNotice, { ShortLimitationsDisclaimer } from './ProductLimitationsNotice';
import SEOHead from './SEOHead';
import JsonLd, { breadcrumbSchema } from './JsonLd';

// Map medication slug → routeKey (for canonical / hreflang building).
const SLUG_ROUTE_KEY = {
  hydrocortisone: 'hydrocortisoneCase',
  valtoco: 'valtocoCase',
  epipen: 'epipenCase',
  naloxone: 'naloxoneCase',
};

const MedicationLandingPage = ({ medication }) => {
  const { language } = useLanguage();
  const content = language === 'fr' ? medication.fr : medication.en;
  const routeKey = SLUG_ROUTE_KEY[medication.slug];

  return (
    <main className="pt-20 sm:pt-24">
      {routeKey && (
        <>
          <SEOHead
            routeKey={routeKey}
            language={language}
            title={`${content.title} | MyGuardPod`}
            description={content.subtitle}
          />
          <JsonLd data={breadcrumbSchema([{ name: content.title, routeKey }], language)} />
        </>
      )}
      {/* Hero */}
      <section className="bg-[#1a1a1a] py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 bg-[#E31B23] text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
              <Shield className="w-4 h-4" />
              {language === 'fr' ? 'Protection MyGuardPod' : 'MyGuardPod Protection'}
            </span>
            <h1 className="font-outfit font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mb-6">
              {content.title}
            </h1>
            <p className="font-manrope text-xl text-neutral-300 mb-8">
              {content.subtitle}
            </p>
            <a
              href={medication.etsyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 bg-[#E31B23] hover:bg-[#c8171e] text-white px-6 py-3 rounded-full font-bold transition-all duration-300 active:scale-95"
              data-testid={`${medication.slug}-etsy-cta`}
            >
              <FaEtsy className="w-5 h-5" />
              <span>{language === 'fr' ? 'Acheter sur Etsy' : 'Shop on Etsy'}</span>
            </a>
            <ShortLimitationsDisclaimer className="mt-4 text-neutral-400 max-w-xl" />
          </motion.div>
        </div>
      </section>

      {/* What & Why */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {/* About the medication */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-outfit font-bold text-2xl sm:text-3xl text-[#1a1a1a] mb-6">
                {content.aboutTitle}
              </h2>
              <p className="font-manrope text-neutral-600 leading-relaxed text-lg">
                {content.aboutText}
              </p>
            </motion.div>

            {/* Why protection matters */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-outfit font-bold text-2xl sm:text-3xl text-[#1a1a1a] mb-6">
                {content.whyTitle}
              </h2>
              <p className="font-manrope text-neutral-600 leading-relaxed mb-6">
                {content.whyText}
              </p>
              <ul className="space-y-3">
                {content.whyPoints.map((point, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#E31B23] mt-0.5 flex-shrink-0" />
                    <span className="font-manrope text-neutral-700">{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* How MyGuardPod helps */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-outfit font-bold text-2xl sm:text-3xl text-[#1a1a1a] mb-6">
                {content.howTitle}
              </h2>
              <p className="font-manrope text-neutral-600 leading-relaxed">
                {content.howText}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Product Limitations Notice */}
      <section className="py-12 bg-neutral-50 border-t border-neutral-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <ProductLimitationsNotice />
          {medication.slug === 'hydrocortisone' && (
            <p
              className="font-manrope text-sm text-neutral-600 leading-relaxed text-center"
              data-testid="adrenal-resources-link"
            >
              {language === 'fr' ? (
                <>
                  Vous cherchez des ressources ind\u00e9pendantes sur l\u2019insuffisance surr\u00e9nalienne\u00a0?{' '}
                  <LocLink
                    to="projectAct"
                    hash="#external-adrenal-resources"
                    className="inline-flex items-center gap-1 text-[#E31B23] hover:text-[#c8171e] font-semibold underline decoration-dotted underline-offset-4"
                  >
                    Consulter les organismes reconnus
                    <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                  </LocLink>
                </>
              ) : (
                <>
                  Looking for independent adrenal-insufficiency resources?{' '}
                  <LocLink
                    to="projectAct"
                    hash="#external-adrenal-resources"
                    className="inline-flex items-center gap-1 text-[#E31B23] hover:text-[#c8171e] font-semibold underline decoration-dotted underline-offset-4"
                  >
                    View recognized patient organizations
                    <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                  </LocLink>
                </>
              )}
            </p>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24 bg-[#E31B23]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-outfit font-black text-3xl sm:text-4xl text-white mb-6">
            {content.ctaTitle}
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={medication.etsyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center space-x-2 bg-white text-[#E31B23] hover:bg-neutral-100 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 active:scale-95"
              data-testid={`${medication.slug}-bottom-cta`}
            >
              <FaEtsy className="w-5 h-5" />
              <span>{language === 'fr' ? 'Acheter sur Etsy' : 'Shop on Etsy'}</span>
            </a>
            <LocLink
              to="products"
              className="inline-flex items-center justify-center space-x-2 border-2 border-white text-white hover:bg-white hover:text-[#E31B23] px-8 py-4 rounded-full font-bold text-lg transition-all duration-300"
            >
              <span>{language === 'fr' ? 'Tous les produits' : 'All Products'}</span>
              <ChevronRight className="w-5 h-5" />
            </LocLink>
          </div>
        </div>
      </section>
    </main>
  );
};

export default MedicationLandingPage;
